import dynamic from 'next/dynamic';
import Head from 'next/head';
import Layout from '@/components/Layout';
import MetaSeo from '@/components/MetaSeo';

const CaseStudyDetail = dynamic(() => import('@/components/CaseStudies/Detail'));

const SITE_URL = 'https://eyefire.vn';
const SUPPORTED_LOCALES = ['vi', 'en', 'jp'];
const DEFAULT_LOCALE = 'vi';
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/images/eyefire_logo.png`;

function buildLocaleUrl(locale, path) {
  const suffix = path || '';
  return locale === DEFAULT_LOCALE
    ? `${SITE_URL}${suffix}`
    : `${SITE_URL}/${locale}${suffix}`;
}

function buildAlternates(slug, translations) {
  const list = Array.isArray(translations) ? translations : [];
  const bySign = {};
  list.forEach((t) => {
    if (t && t.sign && t.slug) bySign[t.sign] = t.slug;
  });
  // Always include the current slug for the locale that served this request,
  // even if it isn't explicitly present in `translations`.
  const map = {};
  SUPPORTED_LOCALES.forEach((loc) => {
    const localized = bySign[loc];
    if (localized) {
      map[loc] = buildLocaleUrl(loc, `/case-studies/${localized}`);
    }
  });
  return map;
}

function Page({ caseStudy, related, slug, sign, translations }) {
  const locale = sign || DEFAULT_LOCALE;
  const title = caseStudy
    ? `${caseStudy.title} | EYEFIRE`
    : 'Case Study | EYEFIRE';
  const description =
    (caseStudy && caseStudy.seo_description) ||
    (caseStudy && caseStudy.title) ||
    'EYEFIRE - Safety AI case studies';
  const image =
    (caseStudy && (caseStudy.image || caseStudy.thumbnail)) || DEFAULT_OG_IMAGE;
  const canonical = buildLocaleUrl(locale, `/case-studies/${slug}`);
  const alternates = buildAlternates(slug, translations);

  const jsonLd = caseStudy
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: caseStudy.title,
        description,
        image: image ? [image] : undefined,
        author: { '@type': 'Organization', name: 'EYEFIRE' },
        publisher: {
          '@type': 'Organization',
          name: 'EYEFIRE',
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/assets/images/eyefire_logo.png`,
          },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
        datePublished: caseStudy.publish_date || caseStudy.createdAt || undefined,
        dateModified: caseStudy.updatedAt || caseStudy.publish_date || undefined,
      }
    : null;

  return (
    <>
      <MetaSeo
        url={canonical}
        type='article'
        title={title}
        description={description}
        image={image}
      />
      <Head>
        <link rel='canonical' href={canonical} key='canonical' />
        {SUPPORTED_LOCALES.map((loc) => {
          const href = alternates[loc];
          if (!href) return null;
          return (
            <link
              key={loc}
              rel='alternate'
              hrefLang={loc === 'jp' ? 'ja' : loc}
              href={href}
            />
          );
        })}
        {alternates[DEFAULT_LOCALE] ? (
          <link
            rel='alternate'
            hrefLang='x-default'
            href={alternates[DEFAULT_LOCALE]}
          />
        ) : null}
        {jsonLd ? (
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />
        ) : null}
      </Head>
      <CaseStudyDetail
        slug={slug}
        caseStudy={caseStudy}
        related={related}
        translations={translations || []}
      />
    </>
  );
}

Page.getLayout = function getLayout(page) {
  return (
    <Layout web='case-studies' webViewMobile={true}>
      {page}
    </Layout>
  );
};

export async function getServerSideProps(context) {
  const slug = context.params && context.params.slug;
  const headers = {
    'Content-Type': 'application/json',
    locale: context.locale || 'vi',
  };

  try {
    const [detailRes, relatedRes] = await Promise.all([
      fetch(
        `${process.env.NEXT_PUBLIC_API_SERVER_URL}/app/case_study/${encodeURIComponent(
          slug
        )}`,
        { headers }
      ),
      fetch(
        `${process.env.NEXT_PUBLIC_API_SERVER_URL}/app/case_study/related?slug=${encodeURIComponent(
          slug
        )}&limit=3`,
        { headers }
      ),
    ]);

    const detailJson = await detailRes.json();
    if (!detailJson || detailJson.code !== 200 || !detailJson.data) {
      return { notFound: true };
    }
    let relatedRows = [];
    try {
      const relatedJson = await relatedRes.json();
      if (relatedJson && relatedJson.code === 200) {
        relatedRows = relatedJson.data || [];
      }
    } catch (_e) {
      // related is optional
    }
    const data = detailJson.data;
    return {
      props: {
        caseStudy: data,
        related: relatedRows,
        slug,
        sign: context.locale || 'vi',
        translations: (data && data.translations) || [],
      },
    };
  } catch (error) {
    console.log('case-study detail fetch failed', error && error.message);
    return { notFound: true };
  }
}

export default Page;
